import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2,
  Briefcase,
  UsersRound,
  CalendarClock,
  MonitorSmartphone,
  Users,
  Mail,
  Upload,
  CheckCircle2,
  Circle,
  ChevronDown,
  ChevronUp,
  Sparkles,
  X,
  ArrowRight,
  Crown,
  ListChecks,
} from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Progress } from "./ui/progress";
import { apiClient, useAuth } from "../App";
import { toast } from "sonner";

// -----------------------------------------------------------------------------
// Setup checklist rendered on /dashboard for tenants who haven't completed
// (or dismissed) initial onboarding. Once dismissed, a compact floating pill
// stays on the dashboard so the owner can resume without hunting through
// Settings — the checklist only fully disappears at 100% complete.
// -----------------------------------------------------------------------------

const STEPS = [
  {
    key: "company_configured",
    label: "Set up your company",
    hint: "Give your business a name, timezone, and store hours. This is the foundation everything else builds on.",
    example: "e.g. \"Sunny Bakery\" · Eastern Time · Mon-Fri 6am-6pm",
    icon: Building2,
    to: "/settings?tab=company",
  },
  {
    key: "pay_group_created",
    label: "Create a Pay Group",
    hint: "A Pay Group defines how a team is paid: cadence (weekly, biweekly), whether time approval is required, and the standard workday length. Employees are assigned to one Pay Group.",
    example: "e.g. \"Weekly W-2 staff\" — paid every Friday · 8 hrs/workday",
    icon: Briefcase,
    to: "/pay-groups?onboarding=1",
  },
  {
    key: "schedule_group_created",
    label: "Create a Schedule Group",
    hint: "A Schedule Group is a team that works together on the schedule board (like a department). Shifts are assigned to a group so only relevant employees see them.",
    example: "e.g. \"Front of House\", \"Kitchen\", \"Weekend Crew\"",
    icon: UsersRound,
    to: "/shifts?onboarding=1&tab=groups",
  },
  {
    key: "shift_created",
    label: "Add a shift template",
    hint: "A Shift is a recurring block of hours. Once created, you can drag it onto the weekly schedule to assign employees.",
    example: "e.g. \"Weekday Open 6am-2pm\" · \"Weekend Close 4pm-11pm\"",
    icon: CalendarClock,
    to: "/shifts?onboarding=1",
  },
  {
    key: "kiosk_registered",
    label: "Register a kiosk device",
    hint: "Any tablet or computer where employees clock in with their PIN. You have two options: (1) Register This Device — if you're setting up on the very tablet employees will use (the button trusts the browser you're on right now). (2) Register a Remote Device — generates a 6-digit code you can text or email; the person at the kiosk opens your /clock link, enters the code, and it's trusted forever.",
    example: "e.g. Register the counter iPad → employees just tap their PIN on it. Or text the 6-digit code to a manager at another store so they can register that store's tablet without you being there.",
    icon: MonitorSmartphone,
    to: "/settings?tab=timeclock#kiosk-devices",
    starterOnly: true,
  },
  {
    key: "employee_added",
    label: "Add your employees",
    hint: "Each employee needs a name, email, PIN, and — critically — a Pay Group and Schedule Group. The fastest way is the CSV import template, which pre-fills the Pay Groups and Schedule Groups you already set up.",
    example: "e.g. Import 12 employees at once from a template with columns for email, schedule group, and pay group.",
    icon: Users,
    to: "/employees?onboarding=1",
  },
  {
    key: "invite_sent",
    label: "Invite employees to sign in",
    hint: "Send each employee a magic-link email so they can view their schedule, request swaps, and check their pay from their own device.",
    example: "Employees sign in with the emailed link — no password needed.",
    icon: Mail,
    to: "/team?onboarding=1",
  },
  {
    key: "historical_imported",
    label: "Import historical hours & payments (optional)",
    hint: "Skip if you're starting fresh. If you have past payroll data — hours worked or payments already made — import it now so employee balances are accurate from day one. Templates auto-populate the employees you just added.",
    example: "e.g. Upload last month's hours + payments so today's dashboard shows the correct running balance.",
    icon: Upload,
    to: "/settings?tab=import",
    optional: true,
  },
];

export const OnboardingChecklist = () => {
  const navigate = useNavigate();
  const { tenant } = useAuth();
  const [state, setState] = useState({ loading: true, data: null });
  const [expanded, setExpanded] = useState({});
  const [minimized, setMinimized] = useState(false);
  const [skipHistLoading, setSkipHistLoading] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await apiClient.get("/onboarding/status");
      setState({ loading: false, data: res.data });
    } catch {
      setState({ loading: false, data: null });
    }
  }, []);

  // Re-fetch whenever the tenant becomes available or its identity changes.
  // First render after a fresh company-setup wizard fires with no tenant,
  // then the AuthContext refreshes `tenant` — this effect picks that up
  // so the checklist appears *immediately* on the dashboard without the
  // user needing to hard-refresh.
  useEffect(() => {
    fetchStatus();
  }, [fetchStatus, tenant?.tenant_id]);

  const dismiss = async () => {
    try {
      await apiClient.post("/onboarding/dismiss");
      setState((s) => ({ ...s, data: { ...s.data, dismissed: true } }));
      // Show a floating resume pill; don't send them hunting through Settings.
      setMinimized(false);
    } catch {
      toast.error("Couldn't dismiss right now");
    }
  };

  const restore = async () => {
    try {
      await apiClient.post("/onboarding/restore");
      await fetchStatus();
    } catch {
      toast.error("Couldn't restore checklist");
    }
  };

  const skipHistorical = async () => {
    setSkipHistLoading(true);
    try {
      await apiClient.post("/onboarding/skip-historical");
      toast.success("Skipped — you can always import later from Settings → Import");
      await fetchStatus();
    } catch {
      toast.error("Couldn't skip right now");
    } finally {
      setSkipHistLoading(false);
    }
  };

  const goTo = (step) => {
    // Support hash-anchored deep links (e.g. /settings?tab=timeclock#kiosk-devices)
    // so users land inside the exact sub-section, not just the tab top.
    // React Router's `navigate` sets location.hash but browsers only scroll
    // to a matching #id on the *initial* load — after that we scroll ourselves.
    navigate(step.to);
    if (step.to.includes("#")) {
      const hash = step.to.split("#")[1];
      // Give the target tab time to render before scrolling.
      setTimeout(() => {
        const el = document.getElementById(hash);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 400);
    }
  };

  const toggle = (key) => {
    setExpanded((e) => ({ ...e, [key]: !e[key] }));
  };

  if (state.loading) return null;
  const data = state.data;
  if (!data) return null;
  // Only fully hide once the owner has finished setup. Dismissal shows a
  // floating "resume" pill instead of vanishing entirely.
  if (data.percent_complete === 100) return null;

  const progress = data.progress || {};
  const isFree = data.is_free_plan;

  // Dismissed → render the compact floating "Resume onboarding" pill so the
  // owner always has one-click access back into setup from the dashboard.
  if (data.dismissed && !minimized) {
    return (
      <div
        className="fixed bottom-6 right-6 z-40 animate-in fade-in slide-in-from-bottom-4 duration-300"
        data-testid="onboarding-resume-pill"
      >
        <button
          type="button"
          onClick={restore}
          className="group flex items-center gap-3 rounded-full bg-[#0D9488] hover:bg-[#14B8A6] shadow-lg shadow-teal-500/30 pl-3 pr-5 py-3 text-white transition-all hover:shadow-xl"
          data-testid="onboarding-resume-btn"
        >
          <div className="relative">
            <div className="absolute inset-0 rounded-full bg-white/20 animate-ping" />
            <div className="relative w-8 h-8 rounded-full bg-white flex items-center justify-center">
              <ListChecks className="w-4 h-4 text-[#0D9488]" />
            </div>
          </div>
          <div className="text-left">
            <p className="text-sm font-semibold leading-tight">Resume Setup</p>
            <p className="text-[10px] opacity-90 leading-tight">
              {data.steps_done} of {data.steps_total} · {data.percent_complete}%
            </p>
          </div>
        </button>
      </div>
    );
  }

  return (
    <div className="animate-in fade-in slide-in-from-top-2 duration-300" data-testid="onboarding-checklist">
      <Card className="border-2 border-[#0D9488]/30 shadow-lg bg-gradient-to-br from-teal-50 to-white overflow-hidden">
        <CardContent className="p-5">
          {/* Header */}
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#0D9488] flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-[#0A192F] font-['Manrope'] text-lg">Get started with Nirvanta</h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  A quick 8-step setup so your first payroll runs smoothly.
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={dismiss}
              data-testid="onboarding-dismiss-btn"
              className="text-slate-400 hover:text-slate-600 -mr-2"
              title="Minimize — a resume button will stay on your dashboard"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>

          {/* Progress bar */}
          <div className="mt-4">
            <div className="flex items-center justify-between text-xs text-slate-600 mb-1.5">
              <span data-testid="onboarding-progress-label">
                <span className="font-semibold text-[#0A192F]">{data.steps_done}</span> of{" "}
                {data.steps_total} done · {data.percent_complete}%
              </span>
              {data.percent_complete >= 75 && (
                <span className="text-emerald-600 font-medium flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> Almost there!
                </span>
              )}
            </div>
            <Progress value={data.percent_complete} className="h-2 bg-slate-200" data-testid="onboarding-progress-bar" />
          </div>

          {/* Step list */}
          <ul className="mt-5 space-y-1.5" data-testid="onboarding-step-list">
            {STEPS.map((step, idx) => {
              const done = !!progress[step.key];
              const isKioskGated = step.starterOnly && isFree;
              const isOpen = expanded[step.key];
              const Icon = step.icon;
              const isHistoricalStep = step.key === "historical_imported";

              return (
                <li
                  key={step.key}
                  className={`rounded-md border transition-colors ${
                    done
                      ? "border-emerald-100 bg-emerald-50/30"
                      : "border-slate-200 bg-white hover:border-[#0D9488]/40 hover:shadow-sm"
                  }`}
                  data-testid={`onboarding-step-${step.key}`}
                >
                  <div className="flex items-center gap-3 px-3 py-2.5">
                    <div className="shrink-0">
                      {done ? (
                        <CheckCircle2 className="w-5 h-5 text-emerald-500" data-testid={`step-${step.key}-done`} />
                      ) : (
                        <Circle className="w-5 h-5 text-slate-300" />
                      )}
                    </div>
                    <div className="shrink-0 w-7 h-7 rounded-md bg-white border border-slate-200 flex items-center justify-center">
                      <Icon className="w-3.5 h-3.5 text-slate-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className={`text-sm font-medium ${
                            done ? "text-slate-500 line-through" : "text-[#0A192F]"
                          }`}
                        >
                          {idx + 1}. {step.label}
                        </span>
                        {isKioskGated && !done && (
                          <span className="inline-flex items-center gap-0.5 rounded-full bg-blue-100 text-blue-700 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide">
                            <Sparkles className="w-2.5 h-2.5" /> Starter+
                          </span>
                        )}
                        {step.optional && !done && (
                          <span className="inline-flex items-center rounded-full bg-slate-100 text-slate-600 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide">
                            Optional
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="shrink-0 flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => toggle(step.key)}
                        className="text-slate-400 hover:text-slate-600 text-xs px-2 py-1 rounded hover:bg-slate-100"
                        data-testid={`step-${step.key}-hint-toggle`}
                      >
                        {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                      {!done && (
                        <>
                          {isHistoricalStep && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-slate-500 hover:text-slate-700 h-8 px-2"
                              onClick={skipHistorical}
                              disabled={skipHistLoading}
                              data-testid="step-historical-skip-btn"
                            >
                              {skipHistLoading ? "Skipping…" : "Skip"}
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-[#0D9488] hover:bg-[#0D9488]/10 h-8 px-2"
                            onClick={() => goTo(step)}
                            data-testid={`step-${step.key}-go-btn`}
                          >
                            {isKioskGated ? (
                              <>
                                <Crown className="w-3.5 h-3.5 mr-1" /> Upgrade
                              </>
                            ) : (
                              <>
                                Go <ArrowRight className="w-3.5 h-3.5 ml-1" />
                              </>
                            )}
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  {isOpen && (
                    <div
                      className="px-3 pb-3 pt-0 animate-in fade-in slide-in-from-top-1 duration-200"
                      data-testid={`step-${step.key}-hint`}
                    >
                      <div className="ml-8 border-l-2 border-[#0D9488]/30 pl-3 space-y-1">
                        <p className="text-xs text-slate-600 leading-relaxed">{step.hint}</p>
                        <p className="text-xs text-slate-500 italic">{step.example}</p>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>

          {/* Refresh helper */}
          <div className="mt-4 flex items-center justify-between text-xs">
            <span className="text-slate-400">
              Progress updates automatically as you complete each step.
            </span>
            <button
              type="button"
              onClick={fetchStatus}
              className="text-[#0D9488] hover:underline"
              data-testid="onboarding-refresh-btn"
            >
              Refresh
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OnboardingChecklist;
